"use client";

import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CurrentWeekData,
  WeeklyBatchesData,
} from "../../api/fetchCampaignStatusMetadata/types";
import { formatCurrency } from "@/utils/formatCurrency";

interface ActiveCampaignsChartProps {
  weeklyBatchesData: WeeklyBatchesData | null;
  activeCampaignsCount: number;
  currentWeekData: CurrentWeekData | null;
}

interface ChartDataItem {
  week: string;
  campaigns: number;
  cost: number;
  quantitySent: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: ChartDataItem;
  }>;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-green-50 dark:bg-gray-800 p-2.5 border border-green-200 dark:border-gray-700 rounded shadow-md">
        <p className="text-lg font-bold text-green-900 dark:text-gray-100 mb-1">
          Week {data.week}
        </p>
        <p className="text-sm text-green-800 dark:text-gray-300">
          Cost: {formatCurrency(data.cost)}
        </p>
        <p className="text-sm text-green-800 dark:text-gray-300">
          Quantity Sent: {data.quantitySent.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

interface CustomTickProps {
  x?: number;
  y?: number;
  payload?: {
    value: string;
  };
  currentWeekNumber?: number;
}

const CustomTick: React.FC<CustomTickProps> = ({
  x = 0,
  y = 0,
  payload,
  currentWeekNumber,
}) => {
  const weekNumber = payload?.value;
  const isCurrentWeek = weekNumber === String(currentWeekNumber);

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={16}
        fill="#16a34a"
        fontSize={12}
        textAnchor="middle"
        x={0}
        y={0}
      >
        {weekNumber}
      </text>
      {isCurrentWeek && <circle cx={0} cy={24} fill="#ef4444" r={3} />}
    </g>
  );
};

export const WeeklyStatisticsChart: React.FC<ActiveCampaignsChartProps> = ({
  weeklyBatchesData,
  activeCampaignsCount,
  currentWeekData,
}) => {
  const chartData = useMemo<ChartDataItem[]>(() => {
    if (!weeklyBatchesData) {
      return [];
    }

    const sortedWeeks = Object.entries(weeklyBatchesData).sort((a, b) => {
      const weekA = parseInt(a[0].replace("week ", ""));
      const weekB = parseInt(b[0].replace("week ", ""));
      return weekA - weekB;
    });

    const last12Weeks = sortedWeeks.slice(-12);

    return last12Weeks.map(([weekKey, data]) => ({
      week: weekKey.replace("week ", ""),
      campaigns: data.batchIds.length,
      cost: parseFloat(data.totalCost),
      quantitySent: data.totalQuantitySent,
    }));
  }, [weeklyBatchesData]);

  if (!weeklyBatchesData || chartData.length === 0) {
    return null;
  }

  return (
    <div className="bg-green-50 dark:bg-gray-800 rounded-lg p-6 border border-green-200 dark:border-gray-700">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide">
            Active Campaigns
          </h3>
          <div className="mt-2">
            <div className="text-3xl font-bold text-green-900 dark:text-green-100">
              {activeCampaignsCount}
            </div>
            <div className="text-sm text-green-700 dark:text-green-400 mt-1">
              Currently running
            </div>
            <div className="text-xs text-green-700 dark:text-green-400 mt-1">
              Last 12 weeks trend
            </div>
          </div>
        </div>
        {currentWeekData && (
          <div className="text-right">
            <div className="text-sm font-semibold text-green-900 dark:text-green-100">
              Week {currentWeekData.weekNumber}
            </div>
            <div className="text-xs text-green-700 dark:text-green-400">
              {currentWeekData.startDate} - {currentWeekData.endDate}
            </div>
          </div>
        )}
      </div>

      <ResponsiveContainer height={120} width="100%">
        <BarChart
          data={chartData}
          margin={{ top: 5, right: 5, left: 5, bottom: 15 }}
        >
          <XAxis
            axisLine={false}
            dataKey="week"
            stroke="#16a34a"
            tick={(props) => (
              <CustomTick
                {...props}
                currentWeekNumber={currentWeekData?.weekNumber}
              />
            )}
            tickLine={false}
          />
          <YAxis domain={[0, "auto"]} hide />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: "transparent" }}
          />
          <Bar dataKey="cost" fill="#16a34a" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
